/**
 * Satellite Imagery Service
 *
 * Multi-source STAC client for Every.Farm. Searches and normalizes scenes from:
 *
 *   - Element84 Earth Search (AWS Open Data)
 *       • Sentinel-2 L2A (optical, 10m) — collection: sentinel-2-l2a
 *
 *   - Microsoft Planetary Computer (free signed Azure COGs)
 *       • Sentinel-1 RTC (SAR, 10m)                — collection: sentinel-1-rtc
 *       • Landsat 8/9 C2 L2 (optical, 30m)         — collection: landsat-c2-l2
 *       • NASA HLS v2.0 L30 / S30 (optical, 30m)   — hls2-l30 / hls2-s30
 *       • MODIS MOD13Q1 (vegetation indices, 250m) — modis-13Q1-061
 *       • USDA NAIP (aerial, 0.6m, US only)        — naip
 *       • Daymet Daily N. America (1km climate)    — daymet-daily-na
 *       • gNATSGO soils (10m, US)                  — gnatsgo-rasters
 *       • USGS 3DEP DEM (10m/30m, US)              — 3dep-seamless
 *       • Impact Observatory LULC (10m, annual)    — io-lulc-9-class
 *       • ESA WorldCover (10m, 2020/2021)          — esa-worldcover
 *
 * Supported by NASA Acres (https://nasaacres.org) — NASA's Applied Sciences
 * consortium for U.S. agriculture. This service prefers free, open archives.
 */
export default class SatelliteImageryService {
  constructor() {
    // STAC endpoints
    this.endpoints = {
      earthSearch: 'https://earth-search.aws.element84.com/v1',
      nasaCmrStac: 'https://cmr.earthdata.nasa.gov/stac/LPCLOUD',
      planetaryComputer: 'https://planetarycomputer.microsoft.com/api/stac/v1'
    }

    // Microsoft Planetary Computer SAS endpoints.
    //  - /sign   : per-href signing (rate-limited; avoid in bulk)
    //  - /token  : per-collection token (1 call covers all assets in that
    //              collection for ~1 hour → vastly fewer requests)
    // Anonymous use is free. Supplying an MPC subscription key via
    // VUE_APP_PLANETARY_COMPUTER_KEY raises the rate limit substantially.
    this.mpcSignEndpoint = 'https://planetarycomputer.microsoft.com/api/sas/v1/sign'
    this.mpcTokenEndpoint = 'https://planetarycomputer.microsoft.com/api/sas/v1/token'
    this.mpcSubscriptionKey =
      (typeof process !== 'undefined' && process.env && process.env.VUE_APP_PLANETARY_COMPUTER_KEY) || null
    this.mpcTokenCache = new Map()      // collectionId -> { token, expiry }
    this.mpcTokenInFlight = new Map()   // collectionId -> Promise (dedup)
    this.mpcSignCache = new Map()       // href -> { signed, expiry } (fallback only)

    // Catalog of supported collections. `endpoint` controls routing, `kind`
    // controls how scenes are rendered / what indices are available.
    this.collections = {
      'sentinel-2-l2a': {
        id: 'sentinel-2-l2a',
        endpoint: 'earthSearch',
        label: 'Sentinel-2 L2A',
        agency: 'ESA Copernicus',
        kind: 'optical',
        resolution: '10 m',
        icon: '🛰️',
        description: 'Multispectral optical imagery (10–60 m). Best for NDVI, NDRE, vegetation vigor.'
      },
      'sentinel-1-rtc': {
        id: 'sentinel-1-rtc',
        endpoint: 'planetaryComputer',
        label: 'Sentinel-1 RTC (SAR)',
        agency: 'ESA Copernicus · Microsoft PC',
        kind: 'sar',
        resolution: '10 m',
        icon: '📡',
        description: 'C-band SAR, radiometrically terrain-corrected. Cloud-penetrating; proxy for soil moisture and canopy structure. Served as signed COGs by Microsoft Planetary Computer.'
      },
      'landsat-c2-l2': {
        id: 'landsat-c2-l2',
        endpoint: 'planetaryComputer',
        label: 'Landsat 8/9 C2 L2',
        agency: 'NASA / USGS · Microsoft PC',
        kind: 'optical',
        resolution: '30 m',
        icon: '🌍',
        description: 'Surface reflectance from NASA/USGS Landsat Collection 2 Level-2 — 50+ year archive. Signed COGs via Planetary Computer.'
      },
      'modis-13Q1-061': {
        id: 'modis-13Q1-061',
        endpoint: 'planetaryComputer',
        label: 'MODIS Vegetation Indices (250 m, 16-day)',
        agency: 'NASA · Microsoft PC',
        kind: 'optical',
        resolution: '250 m',
        icon: '🌱',
        description: 'MODIS MOD13Q1 — 16-day NDVI/EVI composites at 250 m. Ideal for phenology & multi-decade time series.'
      },
      'naip': {
        id: 'naip',
        endpoint: 'planetaryComputer',
        label: 'USDA NAIP (aerial, US only)',
        agency: 'USDA · Microsoft PC',
        kind: 'optical',
        resolution: '0.6 m',
        icon: '✈️',
        description: 'National Agriculture Imagery Program — sub-meter 4-band aerial photography of the continental U.S.'
      },
      'daymet-daily-na': {
        id: 'daymet-daily-na',
        endpoint: 'planetaryComputer',
        label: 'Daymet — Daily Climate (N. America)',
        agency: 'NASA ORNL · Microsoft PC',
        kind: 'climate',
        resolution: '1 km',
        icon: '🌡️',
        description: 'Daily surface weather (tmin, tmax, precip, VPD, SWE, srad) at 1 km. Research-grade, ~1–2 year release lag — good for historical GDD / frost-risk context, not real-time monitoring. Use "Load latest" to bypass date filters.',
        archive: true // flag for UI: advanced/historical, not in default picker
      },
      'gnatsgo-rasters': {
        id: 'gnatsgo-rasters',
        endpoint: 'planetaryComputer',
        label: 'gNATSGO Soils (US)',
        agency: 'USDA NRCS · Microsoft PC',
        kind: 'soil',
        resolution: '10 m',
        icon: '🟫',
        description: 'Gridded National Soil Survey — soil properties (AWC, pH, organic matter, drainage class, etc.) at 10 m across CONUS.'
      },
      '3dep-seamless': {
        id: '3dep-seamless',
        endpoint: 'planetaryComputer',
        label: '3DEP DEM (USGS, US)',
        agency: 'USGS · Microsoft PC',
        kind: 'terrain',
        resolution: '10 m',
        icon: '⛰️',
        description: 'USGS 3D Elevation Program — seamless 10 m / 30 m bare-earth DEM. Drives slope, aspect, hillshade.'
      },
      'io-lulc-9-class': {
        id: 'io-lulc-9-class',
        endpoint: 'planetaryComputer',
        label: 'Impact Observatory Land Cover (annual, 10 m)',
        agency: 'Impact Observatory · Microsoft PC',
        kind: 'landcover',
        resolution: '10 m',
        icon: '🗺️',
        description: '9-class global land cover at 10 m, updated annually. Useful for masking crop vs. non-crop areas.'
      },
      'esa-worldcover': {
        id: 'esa-worldcover',
        endpoint: 'planetaryComputer',
        label: 'ESA WorldCover (10 m)',
        agency: 'ESA · Microsoft PC',
        kind: 'landcover',
        resolution: '10 m',
        icon: '🗺️',
        description: '11-class global land cover at 10 m (2020 & 2021). Complementary to IO-LULC.'
      },
      'hls2-l30': {
        id: 'hls2-l30',
        endpoint: 'planetaryComputer',
        label: 'NASA HLS-Landsat (L30)',
        agency: 'NASA LP DAAC · Microsoft PC',
        kind: 'optical',
        resolution: '30 m',
        icon: '🇺🇸',
        description: 'Harmonized Landsat Sentinel-2 (Landsat 8/9 harmonized) — NASA HLS v2.0 via Planetary Computer (no Earthdata Login required).'
      },
      'hls2-s30': {
        id: 'hls2-s30',
        endpoint: 'planetaryComputer',
        label: 'NASA HLS-Sentinel (S30)',
        agency: 'NASA LP DAAC · Microsoft PC',
        kind: 'optical',
        resolution: '30 m',
        icon: '🇺🇸',
        description: 'Harmonized Landsat Sentinel-2 (Sentinel-2 harmonized) — NASA HLS v2.0 via Planetary Computer (no Earthdata Login required).'
      }
    }

    // Default active collections (farmer-friendly defaults).
    // Sentinel-1 RTC preferred over GRD — it's previewable in-browser.
    this.defaultCollections = ['sentinel-2-l2a', 'sentinel-1-rtc', 'landsat-c2-l2']

    // Cache for search results
    this.searchCache = new Map()
    this.cacheTimeout = 15 * 60 * 1000 // 15 minutes
  }

  /**
   * Return the full collection catalog as an ordered array (for UI).
   */
  getCollectionCatalog() {
    return Object.values(this.collections)
  }

  /**
   * Search imagery across one or more collections.
   * @param {Object} blockGeometry - GeoJSON geometry
   * @param {number} maxResults - cap on returned scenes (across all collections)
   * @param {Object} dateRange - { startDate, endDate }
   * @param {Array<string>} collectionIds - collection keys; default = optical + SAR mainstream
   */
  async searchLatestImagery(blockGeometry, maxResults = 20, dateRange = null, collectionIds = null) {
    const bbox = this.calculateBoundingBox(blockGeometry)
    const activeIds = (collectionIds && collectionIds.length) ? collectionIds : this.defaultCollections

    console.log('🛰️ Multi-source STAC search', { bbox, dateRange, collections: activeIds })

    const dateKey = dateRange ? `${new Date(dateRange.startDate).toISOString()}-${new Date(dateRange.endDate).toISOString()}` : 'default'
    const cacheKey = `${bbox.join(',')}|${maxResults}|${activeIds.sort().join(',')}|${dateKey}`
    const cached = this.searchCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      console.log('📦 Using cached search results')
      return cached.data
    }

    // Per-collection result cap — fetch a fair share from each active source.
    const perCollection = Math.max(5, Math.ceil(maxResults / activeIds.length) + 2)

    const results = await Promise.all(activeIds.map(id => this.searchCollection(id, bbox, perCollection, dateRange)
      .catch(err => {
        console.warn(`⚠️ Search failed for ${id}:`, err.message)
        return { features: [] }
      })
    ))

    const allFeatures = []
    const perCollectionCounts = {}
    const emptyCollections = []
    results.forEach((res, idx) => {
      const collId = activeIds[idx]
      const coll = this.collections[collId]
      const count = (res && res.features) ? res.features.length : 0
      perCollectionCounts[collId] = count
      if (count === 0) emptyCollections.push(collId)
      if (!res || !res.features) return
      res.features.forEach(f => {
        allFeatures.push({
          ...f,
          source: coll.label,
          sourceIcon: coll.icon,
          agency: coll.agency,
          kind: coll.kind,
          collectionMeta: coll,
          // Normalize the collection field — NASA CMR sometimes omits it from the feature body
          collection: f.collection || collId
        })
      })
    })

    // Sign Planetary Computer asset hrefs in parallel (anonymous SAS tokens, free).
    // Done here so downstream tile/dataset code sees ready-to-fetch URLs.
    await Promise.all(allFeatures
      .filter(f => f.collectionMeta?.endpoint === 'planetaryComputer')
      .map(f => this.signPlanetaryComputerAssets(f).catch(err => {
        console.warn(`⚠️ Signing failed for ${f.id}:`, err.message)
      }))
    )

    // Sort by acquisition date (most recent first)
    allFeatures.sort((a, b) => new Date(b.properties.datetime) - new Date(a.properties.datetime))

    const limited = allFeatures.slice(0, maxResults)

    // Empty results are honest — no mock fallback. The UI shows per-collection
    // "Load latest" banners for any collection that returned zero.
    const result = {
      type: 'FeatureCollection',
      features: limited,
      emptyCollections,
      perCollectionCounts
    }

    this.searchCache.set(cacheKey, { data: result, timestamp: Date.now() })
    console.log(`✅ Returned ${result.features.length} scenes across ${activeIds.length} collection(s)`)
    return result
  }

  /**
   * Dispatch to the right STAC endpoint for a collection.
   */
  async searchCollection(collectionId, bbox, maxResults, dateRange) {
    const coll = this.collections[collectionId]
    if (!coll) throw new Error(`Unknown collection: ${collectionId}`)

    const baseUrl = this.endpoints[coll.endpoint]
    const { startIso, endIso } = this.normalizeDateRange(dateRange)

    // Most STAC APIs accept the same query shape. Cloud cover is optical-only —
    // adding it to SAR queries on some backends causes a 400.
    const params = new URLSearchParams({
      collections: collectionId,
      bbox: bbox.join(','),
      datetime: `${startIso}/${endIso}`,
      limit: String(maxResults)
    })
    if (coll.kind === 'optical') {
      params.append('query[eo:cloud_cover][lte]', '60')
    }

    const getUrl = `${baseUrl}/search?${params.toString()}`
    console.log(`🔍 [${collectionId}] GET`, getUrl)

    try {
      const resp = await fetch(getUrl, { method: 'GET', headers: { Accept: 'application/json' } })
      if (resp.ok) {
        const data = await resp.json()
        console.log(`📊 [${collectionId}] ${data.features?.length || 0} features`)
        return data
      }
      console.warn(`⚠️ [${collectionId}] GET ${resp.status} — falling back to POST`)
    } catch (err) {
      console.warn(`⚠️ [${collectionId}] GET threw:`, err.message)
    }

    // POST fallback
    const body = {
      collections: [collectionId],
      bbox,
      datetime: `${startIso}/${endIso}`,
      limit: maxResults
    }
    if (coll.kind === 'optical') {
      body.query = { 'eo:cloud_cover': { lte: 60 } }
    }
    const postResp = await fetch(`${baseUrl}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
    if (!postResp.ok) {
      const text = await postResp.text()
      throw new Error(`${collectionId} STAC error ${postResp.status}: ${text.slice(0, 200)}`)
    }
    return postResp.json()
  }

  /**
   * Sign every asset href on an MPC STAC feature by appending the
   * per-collection SAS token. One token covers every asset in the collection
   * (≈1 API call per collection per hour), which avoids the anonymous
   * per-href rate limit that `/api/sas/v1/sign` imposes.
   */
  async signPlanetaryComputerAssets(feature) {
    if (!feature || !feature.assets) return feature
    const collectionId = feature.collection || feature.collectionMeta?.id
    if (!collectionId) return feature

    let token
    try {
      token = await this.getPlanetaryComputerToken(collectionId)
    } catch (err) {
      console.warn(`⚠️ MPC token fetch failed for ${collectionId} — falling back to per-href sign:`, err.message)
      return this.signAssetsPerHrefFallback(feature)
    }

    Object.keys(feature.assets).forEach(key => {
      const asset = feature.assets[key]
      if (!asset || !asset.href) return
      if (!/blob\.core\.windows\.net/i.test(asset.href)) return
      if (/[?&](sig=|se=)/.test(asset.href)) return // already signed
      const sep = asset.href.includes('?') ? '&' : '?'
      feature.assets[key].href = `${asset.href}${sep}${token}`
    })
    return feature
  }

  /**
   * Fetch (and cache) the per-collection SAS token. Token response shape:
   *   { "token": "sv=…&sig=…", "msft:expiry": "2026-…Z" }
   */
  async getPlanetaryComputerToken(collectionId) {
    const now = Date.now()
    const cached = this.mpcTokenCache.get(collectionId)
    if (cached && cached.expiry > now + 60 * 1000) return cached.token

    // Dedup concurrent requests for the same collection
    if (this.mpcTokenInFlight.has(collectionId)) {
      return this.mpcTokenInFlight.get(collectionId)
    }
    const url = `${this.mpcTokenEndpoint}/${encodeURIComponent(collectionId)}`
    const headers = { Accept: 'application/json' }
    if (this.mpcSubscriptionKey) {
      headers['Ocp-Apim-Subscription-Key'] = this.mpcSubscriptionKey
    }
    const promise = fetch(url, { method: 'GET', headers })
      .then(async resp => {
        if (!resp.ok) throw new Error(`MPC token ${resp.status}`)
        const data = await resp.json()
        const token = data.token
        const expiry = data['msft:expiry']
          ? new Date(data['msft:expiry']).getTime()
          : Date.now() + 45 * 60 * 1000
        this.mpcTokenCache.set(collectionId, { token, expiry })
        return token
      })
      .finally(() => this.mpcTokenInFlight.delete(collectionId))
    this.mpcTokenInFlight.set(collectionId, promise)
    return promise
  }

  /**
   * Fallback per-href signing (only used if the collection token endpoint
   * fails). Single retry with small concurrency to avoid 429s.
   */
  async signAssetsPerHrefFallback(feature) {
    const entries = Object.entries(feature.assets)
    for (const [key, asset] of entries) {
      if (!asset || !asset.href) continue
      if (!/blob\.core\.windows\.net/i.test(asset.href)) continue
      if (/[?&](sig=|se=)/.test(asset.href)) continue
      try {
        const signed = await this.signPlanetaryComputerHref(asset.href)
        if (signed) feature.assets[key].href = signed
      } catch (err) {
        console.warn(`⚠️ Per-href sign failed for ${key}:`, err.message)
      }
    }
    return feature
  }

  async signPlanetaryComputerHref(href) {
    const cached = this.mpcSignCache.get(href)
    const now = Date.now()
    if (cached && cached.expiry > now + 60 * 1000) return cached.signed

    const url = `${this.mpcSignEndpoint}?href=${encodeURIComponent(href)}`
    const headers = { Accept: 'application/json' }
    if (this.mpcSubscriptionKey) headers['Ocp-Apim-Subscription-Key'] = this.mpcSubscriptionKey
    const resp = await fetch(url, { method: 'GET', headers })
    if (!resp.ok) throw new Error(`MPC sign ${resp.status}`)
    const data = await resp.json()
    const signed = data.href
    const expiry = data['msft:expiry']
      ? new Date(data['msft:expiry']).getTime()
      : now + 45 * 60 * 1000
    this.mpcSignCache.set(href, { signed, expiry })
    return signed
  }

  /**
   * Find the latest N scenes for a specific collection regardless of the
   * caller's date range. Used by the "No results in range — Load latest"
   * escape hatch in the UI. Searches the entire archive by posting with
   * no datetime filter, sorted descending.
   */
  async searchLatestForCollection(collectionId, blockGeometry, maxResults = 8) {
    const coll = this.collections[collectionId]
    if (!coll) throw new Error(`Unknown collection: ${collectionId}`)
    const bbox = this.calculateBoundingBox(blockGeometry)
    const baseUrl = this.endpoints[coll.endpoint]

    const body = {
      collections: [collectionId],
      bbox,
      limit: maxResults,
      // STAC 1.0 sort extension — most providers accept this shape
      sortby: [{ field: 'properties.datetime', direction: 'desc' }]
    }

    let features = []
    try {
      const resp = await fetch(`${baseUrl}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
      if (resp.ok) {
        const data = await resp.json()
        features = data.features || []
      }
    } catch (err) {
      console.warn(`⚠️ load-latest POST failed for ${collectionId}:`, err.message)
    }

    // Fallback 1: GET without datetime (archive-wide, limited)
    if (features.length === 0) {
      const params = new URLSearchParams({
        collections: collectionId,
        bbox: bbox.join(','),
        limit: String(maxResults)
      })
      try {
        const resp = await fetch(`${baseUrl}/search?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        })
        if (resp.ok) {
          const data = await resp.json()
          features = data.features || []
        }
      } catch (err) {
        console.warn(`⚠️ load-latest GET failed for ${collectionId}:`, err.message)
      }
    }

    // Fallback 2: some MPC collections (e.g. daymet-daily-na) require a
    // datetime filter and return 0 without one. Retry with a very wide
    // range that covers the archive's known lifetime.
    if (features.length === 0) {
      const wideStart = '1980-01-01T00:00:00Z'
      const wideEnd = new Date().toISOString()
      const wideParams = new URLSearchParams({
        collections: collectionId,
        bbox: bbox.join(','),
        datetime: `${wideStart}/${wideEnd}`,
        limit: String(maxResults)
      })
      try {
        const resp = await fetch(`${baseUrl}/search?${wideParams.toString()}`, {
          headers: { Accept: 'application/json' }
        })
        if (resp.ok) {
          const data = await resp.json()
          features = data.features || []
          if (features.length > 0) {
            console.log(`🕰️ load-latest for ${collectionId} required wide datetime range`)
          }
        }
      } catch (err) {
        console.warn(`⚠️ load-latest wide-range GET failed for ${collectionId}:`, err.message)
      }
    }

    // Decorate + sort newest-first (fallback if server ignored sortby)
    features = features.map(f => ({
      ...f,
      source: coll.label,
      sourceIcon: coll.icon,
      agency: coll.agency,
      kind: coll.kind,
      collectionMeta: coll,
      collection: f.collection || collectionId
    }))
    features.sort((a, b) => {
      const ad = a.properties?.datetime || a.properties?.end_datetime || a.properties?.start_datetime || ''
      const bd = b.properties?.datetime || b.properties?.end_datetime || b.properties?.start_datetime || ''
      return new Date(bd) - new Date(ad)
    })

    // Sign MPC assets like the main search does
    if (coll.endpoint === 'planetaryComputer') {
      await Promise.all(features.map(f => this.signPlanetaryComputerAssets(f).catch(err => {
        console.warn(`⚠️ sign fail for ${f.id}:`, err.message)
      })))
    }

    return { type: 'FeatureCollection', features }
  }

  normalizeDateRange(dateRange) {
    let start, end
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      start = new Date(dateRange.startDate)
      end = new Date(dateRange.endDate)
    } else {
      end = new Date()
      start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
    }
    return { startIso: start.toISOString(), endIso: end.toISOString() }
  }

  /**
   * Process imagery result — normalized for downstream map/dataset code.
   */
  processImageryResult(feature) {
    const p = feature.properties || {}
    return {
      id: feature.id,
      // Prefer single datetime; fall back to composite window endpoints
      datetime: p.datetime || p.end_datetime || p.start_datetime || null,
      cloudCover: typeof p['eo:cloud_cover'] === 'number' ? p['eo:cloud_cover'] : null,
      satellite: feature.source || 'Unknown',
      agency: feature.agency || '',
      kind: feature.kind || 'optical',
      collection: feature.collection,
      collectionMeta: feature.collectionMeta,
      assets: feature.assets,
      source: feature.source,
      sourceIcon: feature.sourceIcon,
      // Pass raw properties so kind-aware widgets can read start/end_datetime,
      // sar:polarizations, landcover class metadata, etc.
      properties: p
    }
  }

  /**
   * Calculate bounding box from geometry
   */
  calculateBoundingBox(geometry) {
    let minLng = Infinity; let minLat = Infinity; let maxLng = -Infinity; let maxLat = -Infinity

    const visit = (coord) => {
      if (!isNaN(coord[0]) && !isNaN(coord[1])) {
        minLng = Math.min(minLng, coord[0]); minLat = Math.min(minLat, coord[1])
        maxLng = Math.max(maxLng, coord[0]); maxLat = Math.max(maxLat, coord[1])
      }
    }

    if (geometry.type === 'Polygon') {
      geometry.coordinates[0].forEach(visit)
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(p => p[0].forEach(visit))
    }

    return [minLng, minLat, maxLng, maxLat]
  }

  /**
   * Mock result for offline / API-down demos.
   */
  getMockEarthSearchResult(bbox) {
    const baseDate = new Date()
    const mockScenes = [
      {
        id: 'S2B_MSIL2A_20241201T160921_N0511_R140_T17TPG',
        collection: 'sentinel-2-l2a',
        source: 'Sentinel-2 L2A',
        sourceIcon: '🛰️',
        agency: 'ESA Copernicus',
        kind: 'optical',
        properties: {
          datetime: new Date(baseDate.getTime() - 5 * 86400000).toISOString(),
          'eo:cloud_cover': 12.5,
          title: 'Sentinel-2 L2A Scene'
        },
        assets: {
          visual: { href: 'https://sentinel-cogs.s3.amazonaws.com/sentinel-s2-l2a-cogs/17/T/PG/2024/12/S2B_17TPG_20241201_0_L2A/TCI.tif', title: 'True Color Image' },
          red: { href: 'https://sentinel-cogs.s3.amazonaws.com/sentinel-s2-l2a-cogs/17/T/PG/2024/12/S2B_17TPG_20241201_0_L2A/B04.tif', title: 'Red' },
          nir: { href: 'https://sentinel-cogs.s3.amazonaws.com/sentinel-s2-l2a-cogs/17/T/PG/2024/12/S2B_17TPG_20241201_0_L2A/B08.tif', title: 'NIR' }
        }
      },
      {
        id: 'S1A_IW_GRDH_1SDV_20241128T231203_20241128T231228_056789',
        collection: 'sentinel-1-rtc',
        source: 'Sentinel-1 RTC (SAR)',
        sourceIcon: '📡',
        agency: 'ESA Copernicus',
        kind: 'sar',
        properties: {
          datetime: new Date(baseDate.getTime() - 8 * 86400000).toISOString(),
          'sar:instrument_mode': 'IW',
          'sar:polarizations': ['VV', 'VH'],
          title: 'Sentinel-1 GRD Scene'
        },
        assets: {
          vv: { href: 'https://example/s1/vv.tif', title: 'VV polarization' },
          vh: { href: 'https://example/s1/vh.tif', title: 'VH polarization' }
        }
      },
      {
        id: 'LC09_L2SP_017030_20241120_02_T1',
        collection: 'landsat-c2-l2',
        source: 'Landsat 8/9 C2 L2',
        sourceIcon: '🌍',
        agency: 'NASA / USGS',
        kind: 'optical',
        properties: {
          datetime: new Date(baseDate.getTime() - 17 * 86400000).toISOString(),
          'eo:cloud_cover': 15.8,
          title: 'Landsat 9 C2 L2'
        },
        assets: { red: { href: '', title: 'Red' }, nir08: { href: '', title: 'NIR' } }
      }
    ]
    return {
      type: 'FeatureCollection',
      features: mockScenes.map(s => ({ type: 'Feature', ...s }))
    }
  }

  /**
   * Available assets for a scene, adapted to the collection kind.
   * Returns [{ name, title, description, url }]
   */
  getAvailableAssets(feature) {
    if (!feature.assets) return []
    const kind = feature.kind ||
      feature.collectionMeta?.kind ||
      (String(feature.collection).startsWith('sentinel-1') ? 'sar' : 'optical')
    const out = []

    const push = (name) => {
      const a = feature.assets[name]
      if (!a) return
      out.push({
        name,
        title: a.title || this.getAssetTitle(name, kind),
        description: a.description || this.getAssetDescription(name, kind),
        url: a.href
      })
    }

    if (kind === 'sar') {
      ['vv', 'vh', 'hh', 'hv'].forEach(push)
    } else if (String(feature.collection).includes('landsat') || String(feature.collection).startsWith('HLSL')) {
      // Landsat / HLS-Landsat band names
      ;['visual', 'red', 'green', 'blue', 'nir08', 'swir16', 'swir22', 'lwir11', 'B02', 'B03', 'B04', 'B05', 'B06', 'B07'].forEach(push)
    } else {
      // Sentinel-2 / HLS-Sentinel
      if (feature.assets.visual) push('visual')
      ;['red', 'green', 'blue', 'nir', 'B02', 'B03', 'B04', 'B08', 'B8A', 'B11', 'B12'].forEach(push)
    }

    return out
  }

  /**
   * Available vegetation/SAR indices computable from the scene's assets.
   * Returns [{ name, label, formula, description }]
   */
  getAvailableIndices(feature) {
    const assets = feature.assets || {}
    const has = (...keys) => keys.every(k => assets[k])
    const kind = feature.kind ||
      feature.collectionMeta?.kind ||
      (String(feature.collection).startsWith('sentinel-1') ? 'sar' : 'optical')
    const out = []

    if (kind === 'sar') {
      if (has('vv')) out.push({ name: 'VV_dB', label: 'VV backscatter (dB)', formula: '10*log10(VV)', description: 'Surface roughness / structure proxy.' })
      if (has('vh')) out.push({ name: 'VH_dB', label: 'VH backscatter (dB)', formula: '10*log10(VH)', description: 'Canopy volume scattering / biomass proxy.' })
      if (has('vv', 'vh')) out.push({ name: 'RVI', label: 'Radar Vegetation Index', formula: '4*VH / (VV + VH)', description: 'Canopy density / biomass proxy.' })
      return out
    }

    // Optical
    if (has('red', 'nir') || has('B04', 'B08')) out.push({ name: 'NDVI', label: 'NDVI', formula: '(NIR - Red) / (NIR + Red)', description: 'Canopy greenness / vigor.' })
    if (has('B05', 'B08') || has('red', 'nir08')) out.push({ name: 'NDRE', label: 'NDRE (Red Edge)', formula: '(NIR - RedEdge) / (NIR + RedEdge)', description: 'Chlorophyll / nitrogen status.' })
    if (has('green', 'nir') || has('B03', 'B08')) out.push({ name: 'GNDVI', label: 'GNDVI', formula: '(NIR - Green) / (NIR + Green)', description: 'Chlorophyll-sensitive green NDVI.' })
    if (has('B08', 'B11') || has('nir', 'swir16')) out.push({ name: 'NDMI', label: 'NDMI', formula: '(NIR - SWIR) / (NIR + SWIR)', description: 'Canopy moisture.' })
    return out
  }

  getAssetTitle(asset, kind = 'optical') {
    if (kind === 'sar') {
      const sarMap = { vv: 'VV polarization', vh: 'VH polarization', hh: 'HH polarization', hv: 'HV polarization' }
      if (sarMap[asset]) return sarMap[asset]
    }
    const opt = {
      visual: 'True Color Image (RGB)',
      red: 'Red',
      green: 'Green',
      blue: 'Blue',
      nir: 'Near Infrared',
      nir08: 'NIR (0.8 µm)',
      swir16: 'SWIR 1.6 µm',
      swir22: 'SWIR 2.2 µm',
      lwir11: 'Thermal 11 µm',
      B02: 'Band 2 - Blue (490 nm) - 10 m',
      B03: 'Band 3 - Green (560 nm) - 10 m',
      B04: 'Band 4 - Red (665 nm) - 10 m',
      B05: 'Band 5 - Red Edge 1 (705 nm)',
      B06: 'Band 6 - Red Edge 2 (740 nm)',
      B07: 'Band 7 - Red Edge 3 (783 nm)',
      B08: 'Band 8 - NIR (842 nm) - 10 m',
      B8A: 'Band 8A - Narrow NIR (865 nm)',
      B11: 'Band 11 - SWIR (1610 nm)',
      B12: 'Band 12 - SWIR (2190 nm)',
      NDVI: 'NDVI - Vegetation Index'
    }
    return opt[asset] || asset
  }

  getAssetDescription(asset, kind = 'optical') {
    if (kind === 'sar') {
      const m = {
        vv: 'Co-polarized C-band backscatter — sensitive to bare-soil roughness and vertical canopy structure.',
        vh: 'Cross-polarized C-band backscatter — sensitive to volume scattering / canopy biomass.',
        hh: 'HH polarized backscatter.',
        hv: 'HV polarized backscatter.'
      }
      if (m[asset]) return m[asset]
    }
    const opt = {
      B02: 'Blue band (490 nm) — water and atmospheric correction.',
      B03: 'Green band (560 nm) — vegetation vigor (GNDVI).',
      B04: 'Red band (665 nm) — chlorophyll absorption (NDVI).',
      B05: 'Red-edge 1 (705 nm) — chlorophyll / NDRE.',
      B08: 'NIR band (842 nm) — canopy NDVI numerator.',
      B11: 'SWIR band (1610 nm) — canopy water content (NDMI).'
    }
    return opt[asset] || `${asset} band`
  }

  getTileUrl(cogUrl, asset = 'visual') {
    // Direct COG URL — Mapbox / TiTiler handle tiling upstream.
    return cogUrl
  }

  getEvalScript(asset) {
    switch (asset) {
      case 'visual': return 'True Color (RGB) — Red, Green, Blue'
      case 'NDVI': return 'NDVI = (NIR - Red) / (NIR + Red)'
      case 'NDRE': return 'NDRE = (NIR - RedEdge) / (NIR + RedEdge)'
      case 'NDMI': return 'NDMI = (NIR - SWIR) / (NIR + SWIR)'
      case 'RVI': return 'RVI = 4 × VH / (VV + VH)'
      case 'VV_dB':
      case 'VH_dB':
        return '10 × log10(σ0)'
      default: return asset
    }
  }
}
